import { useState } from 'react';
import { MedicationFormData } from '@/components/medication/types/medication';

/**
 * Custom hook for managing medication form state
 * 
 * Provides:
 * - Form data state management
 * - Step navigation
 * - Form utility functions
 */
export const useMedicationForm = (initialData?: Partial<MedicationFormData>) => {
  // Set default form values
  const [formData, setFormData] = useState<MedicationFormData>({
    name: initialData?.name || '',
    dosage: initialData?.dosage || '',
    form: initialData?.form || '',
    frequency: initialData?.frequency || 'daily',
    timeOfDay: initialData?.timeOfDay || [],
    dosesAtTime: initialData?.dosesAtTime || {},
    hasCycle: initialData?.hasCycle || false,
    cycleWeeksOn: initialData?.cycleWeeksOn || 1,
    cycleWeeksOff: initialData?.cycleWeeksOff || 1,
    cycleStartDate: initialData?.cycleStartDate,
    cycleEndDate: initialData?.cycleEndDate,
    foodInstructions: initialData?.foodInstructions || '',
    color: initialData?.color,
    notes: initialData?.notes,
    refillTracking: initialData?.refillTracking || false,
    refillAmount: initialData?.refillAmount,
    refillThreshold: initialData?.refillThreshold || 7,
    refillsRemaining: initialData?.refillsRemaining
  });

  // Step management
  const [currentStep, setCurrentStep] = useState<number>(1);

  // Update form data for a single field
  const updateFormData = (field: keyof MedicationFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle time of day selection and dose counter
  const handleTimeSelection = (timeKey: string) => {
    const isSelected = formData.timeOfDay.includes(timeKey);
    let newTimeOfDay = [...formData.timeOfDay];
    let newDosesAtTime = { ...formData.dosesAtTime };

    if (isSelected) {
      // Increment the dose if the time is already selected
      // Only modify the specific time that was selected
      newDosesAtTime = {
        ...newDosesAtTime,
        [timeKey]: (newDosesAtTime[timeKey] || 1) + 1
      };
    } else {
      // Add the time and set initial dose to 1
      newTimeOfDay.push(timeKey);
      newDosesAtTime = {
        ...newDosesAtTime,
        [timeKey]: 1
      };
    }

    updateFormData('timeOfDay', newTimeOfDay);
    updateFormData('dosesAtTime', newDosesAtTime);
  };

  // Decrement dose for a specific time
  const decrementDose = (timeKey: string) => {
    if (!formData.timeOfDay.includes(timeKey)) return;
    
    const currentDose = formData.dosesAtTime[timeKey] || 0;
    if (currentDose <= 1) return;
    
    const newDosesAtTime = {
      ...formData.dosesAtTime,
      [timeKey]: currentDose - 1
    };
    
    updateFormData('dosesAtTime', newDosesAtTime);
  };

  // Remove a time slot completely
  const removeDoseTime = (timeKey: string) => {
    if (!formData.timeOfDay.includes(timeKey)) return;
    
    const newTimeOfDay = formData.timeOfDay.filter(t => t !== timeKey);
    const newDosesAtTime = { ...formData.dosesAtTime };
    delete newDosesAtTime[timeKey];
    
    updateFormData('timeOfDay', newTimeOfDay);
    updateFormData('dosesAtTime', newDosesAtTime);
  };

  // Reset the form to initial state
  const resetForm = () => {
    setFormData({
      name: '',
      dosage: '',
      form: '',
      frequency: 'daily',
      timeOfDay: [],
      dosesAtTime: {},
      hasCycle: false,
      cycleWeeksOn: 1,
      cycleWeeksOff: 1,
      foodInstructions: '',
      color: undefined,
      notes: '',
      refillTracking: false,
      refillAmount: undefined,
      refillThreshold: 7,
      refillsRemaining: undefined
    });
    setCurrentStep(1);
  };

  return {
    formData,
    currentStep,
    updateFormData,
    handleTimeSelection,
    decrementDose,
    removeDoseTime,
    setCurrentStep,
    resetForm
  };
};
